import { useQuery } from 'react-query';
import { LocationData } from '@/components/organisms/search-bar/search-bar.types';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

interface UseQueryDestinationHierarchyData {
  departures: LocationData['transportationOptions'];
  destinations: LocationData['locations'];
}

interface UseQueryDestinationHierarchyOptions {
  initialData?: UseQueryDestinationHierarchyData;
}

interface UseQueryDestinationHierarchyParameters {
  adultsNumber?: string;
  brand?: Content.Properties.Brand;
  campaignId?: string;
  durationType: string;
  hotelIds?: string;
}

const useQueryDestinationHierarchy = (
  parameters: UseQueryDestinationHierarchyParameters,
  options?: UseQueryDestinationHierarchyOptions,
) => {
  const locale = getLocale();
  const query = useQuery<UseQueryDestinationHierarchyData>(
    [QueryId.DESTINATION_HIERARCHY, parameters, locale],
    async () => {
      const baseUrl = '/api/content-destinations/destination-hierarchy';
      const searchParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryDestinationHierarchy', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters.durationType,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryDestinationHierarchy;
